import type { ListBuilderConfiguration } from '../types';

/**
 * Shared star rating value mapping for recommendation score columns
 */
const STAR_RATING_VALUE_MAPPING = [
	{
		value: '-',
		color: { text: '#d1d5db' },
	},
	{
		value: '●',
		color: { text: '#6b7280' },
	},
	{
		value: '●●',
		color: { text: '#f59e0b' },
	},
	{
		value: '●●●',
		color: { text: '#f59e0b' },
	},
	{
		value: '●●●●',
		color: { text: '#22c55e' },
	},
	{
		value: '●●●●●',
		color: { text: '#22c55e' },
	},
] as const;

/**
 * Shared recommendation level value mapping
 */
const RECOMMENDATION_LEVEL_VALUE_MAPPING = [
	{
		color: { text: '#ffffff', bg: '#16a34a' },
		value: 'Strong',
		displayText: 'Strong',
	},
	{
		color: { text: '#ffffff', bg: '#d97706' },
		value: 'Good',
		displayText: 'Good',
	},
	{
		color: { text: '#ffffff', bg: '#6b7280' },
		value: 'Fair',
		displayText: 'Fair',
	},
] as const;

/**
 * Shared category column definition
 */
const getCategoryColumn = () =>
	({
		id: 'category',
		fields: [
			{
				id: 'category-name-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'category' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'static',
						staticColor: { text: '#3b82f6' },
					},
				},
				displayStyle: 'text',
			},
			{
				id: 'line-break-1',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'item-count-field',
				dataConfig: {
					type: 'template',
					template: '{itemCount} items',
				},
				styleConfig: {
					size: 'xs',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h1', text: 'Category' }],
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'fixed', preset: 'medium' },
		verticalAlignment: 'middle',
	}) as const;

/**
 * Shared chart column definition
 */
const getChartColumn = (idPrefix: string) =>
	({
		id: idPrefix === '' ? 'chart' : `chart${idPrefix}`,
		fields: [
			{
				id: `chart${idPrefix === '' ? '' : `-${idPrefix.toLowerCase()}`}-field`,
				dataConfig: {
					type: 'field',
					field: { fieldName: 'monthlyData' },
					defaultValue: '[]',
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'custom',
				customComponent: { id: 'client-item-history-trend-chart' },
			},
		],
		headers: [
			{
				id: idPrefix === '' ? 'h4' : `h-chart-${idPrefix.toLowerCase()}`,
				text: '12 Month Trend',
			},
		],
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'middle',
	}) as const;

/**
 * Client Category Insights list configuration
 * Multi-view (3 tabs): Previously Purchased, At Risk, Recommended
 * Converted from JSON to TypeScript for type safety
 */
export const CLIENT_CATEGORY_INSIGHTS_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'client-category-previously-purchased',
			cacheTTL: 600000,
			name: 'Previously Purchased',
			type: 'default',
			active: true,
			columns: [
				getCategoryColumn(),
				{
					id: 'purchaseInfo',
					fields: [
						{
							id: 'last-purchase-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPurchasedDate' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								formatOptions: { dateFormat: 'date-short' },
							},
							displayStyle: 'date',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'frequency-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'purchaseFrequency' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h2', text: 'Last Purchase Date' },
						{ id: 'h3', text: 'Frequency' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				{
					id: 'financialMetrics',
					fields: [
						{
							id: 'total-spent-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'totalSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-financial',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'last-six-months-spent-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastSixMonthsSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-financial-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'previous-six-months-spent-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'previousSixMonthsSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h-financial-1', text: 'Total Spent' },
						{ id: 'h-financial-2', text: 'Last 6 Mo' },
						{ id: 'h-financial-3', text: 'Prev 6 Mo' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				{
					id: 'sixMonthComparison',
					fields: [
						{
							id: 'twelve-month-total-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityLast12Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-twelve-month',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'last-six-months-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastSixMonths' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-six-month',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'previous-six-months-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'previousSixMonths' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h-twelve-month', text: '12-Mo Total' },
						{ id: 'h-six-month-1', text: 'Last 6 Mo' },
						{ id: 'h-six-month-2', text: 'Prev 6 Mo' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				getChartColumn(''),
			],
			searchEnabled: true,
			searchPlaceholder: 'Search categories...',
			sortOrder: 0,
		},
		{
			id: 'at-risk-categories',
			cacheTTL: 600000,
			name: 'At Risk',
			type: 'custom',
			active: true,
			filterRules: {
				viewType: { defaultValue: 'atRisk' },
			},
			columns: [
				getCategoryColumn(),
				{
					id: 'purchaseInfo',
					fields: [
						{
							id: 'last-purchase-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPurchasedDate' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								formatOptions: { dateFormat: 'date-short' },
							},
							displayStyle: 'date',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'days-since-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'daysSinceLastPurchase' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-3',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'frequency-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'purchaseFrequency' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h2', text: 'Last Purchase' },
						{ id: 'h3', text: 'Days Since' },
						{ id: 'h4', text: 'Frequency' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantities',
					fields: [
						{
							id: 'twelve-month-qty-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityLast12Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-twelve-atrisk',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'last-six-months-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastSixMonths' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-3',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'previous-six-months-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'previousSixMonths' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h-twelve-mo-qty-atrisk', text: '12-Mo Qty' },
						{ id: 'h4', text: 'Last 6 Mo' },
						{ id: 'h5', text: 'Prev 6 Mo' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				{
					id: 'financialAtRisk',
					fields: [
						{
							id: 'total-spent-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'totalSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-financial-atrisk',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'last-six-months-spent-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastSixMonthsSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-financial-atrisk-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'previous-six-months-spent-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'previousSixMonthsSpent' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 2, currencyDisplay: 'symbol' },
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h-financial-atrisk-1', text: 'Total Spent' },
						{ id: 'h-financial-atrisk-2', text: 'Last 6 Mo' },
						{ id: 'h-financial-atrisk-3', text: 'Prev 6 Mo' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				{
					id: 'avgPerOrderAtRisk',
					fields: [
						{
							id: 'distinct-items-last6-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'distinctItemsLast6Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-avg-atrisk',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'distinct-items-prev6-atrisk-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'distinctItemsPrevious6Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h-avg-atrisk-1', text: 'Last 6 Mo Items' },
						{ id: 'h-avg-atrisk-2', text: 'Prev 6 Mo Items' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'xsmall' },
					verticalAlignment: 'middle',
				},
				getChartColumn('AtRisk'),
			],
			searchEnabled: true,
			searchPlaceholder: 'Search categories...',
			sortOrder: 1,
		},
		{
			id: 'client-category-recommended',
			cacheTTL: 600000,
			name: 'Recommended',
			type: 'custom',
			active: true,
			filterRules: {
				listType: { defaultValue: 'recommended' },
			},
			columns: [
				getCategoryColumn(),
				{
					id: 'recommendationLevel',
					fields: [
						{
							id: 'recommendation-badge',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'recommendationLevel' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'value-mapping' },
								valueMapping: [...RECOMMENDATION_LEVEL_VALUE_MAPPING],
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h-recommendation', text: 'Recommendation' }],
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', value: '120px' },
					verticalAlignment: 'middle',
				},
				{
					id: 'sameBusinessTypeScore',
					fields: [
						{
							id: 'same-business-type-stars',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'sameBusinessTypeStars' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'value-mapping' },
								valueMapping: [...STAR_RATING_VALUE_MAPPING],
							},
							displayStyle: 'text',
						},
						{
							id: 'same-business-type-line-break',
							displayStyle: 'line_break',
						},
						{
							id: 'same-business-type-text',
							dataConfig: {
								type: 'template',
								template:
									'{sameBusinessTypeScore} customers ({sameBusinessTypePercent}%)',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-same-business-type', text: 'Business Match' }],
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', value: '100px' },
					verticalAlignment: 'middle',
				},
				{
					id: 'sameGeographyScore',
					fields: [
						{
							id: 'same-geography-stars',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'sameGeographyStars' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'value-mapping' },
								valueMapping: [...STAR_RATING_VALUE_MAPPING],
							},
							displayStyle: 'text',
						},
						{
							id: 'same-geography-line-break',
							displayStyle: 'line_break',
						},
						{
							id: 'same-geography-text',
							dataConfig: {
								type: 'template',
								template:
									'{sameGeographyScore} customers ({sameGeographyPercent}%)',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-same-geography', text: 'Geography Match' }],
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', value: '100px' },
					verticalAlignment: 'middle',
				},
				{
					id: 'lookalikeScore',
					fields: [
						{
							id: 'lookalike-stars',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lookalikeStars' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'value-mapping' },
								valueMapping: [...STAR_RATING_VALUE_MAPPING],
							},
							displayStyle: 'text',
						},
						{
							id: 'lookalike-line-break',
							displayStyle: 'line_break',
						},
						{
							id: 'lookalike-text',
							dataConfig: {
								type: 'template',
								template: '{lookalikeScore} customers ({lookalikePercent}%)',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-lookalike', text: 'Similar Clients' }],
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', value: '100px' },
					verticalAlignment: 'middle',
				},
				{
					id: 'generalPopularityScore',
					fields: [
						{
							id: 'general-popularity-stars',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'popularityStars' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'value-mapping' },
								valueMapping: [...STAR_RATING_VALUE_MAPPING],
							},
							displayStyle: 'text',
						},
						{
							id: 'general-popularity-line-break',
							displayStyle: 'line_break',
						},
						{
							id: 'general-popularity-text',
							dataConfig: {
								type: 'template',
								template:
									'{generalPopularityScore} customers ({popularityPercent}%)',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-general-popularity', text: 'Overall Popularity' }],
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', value: '100px' },
					verticalAlignment: 'middle',
				},
			],
			searchEnabled: true,
			searchPlaceholder: 'Search recommended categories...',
			sortOrder: 2,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
};
